import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Search, Loader2, CheckCircle2, AlertCircle, Shield } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Componente para lidar com o retorno do login via Discord
 * Processa o token da URL e tenta autenticar o usuário
 * Design Premium & Moderno
 */
const DiscordCallback = () => {
    const { loginWithDiscord } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const processedRef = useRef(false);
    const [currentTextIndex, setCurrentTextIndex] = useState(0);
    const [status, setStatus] = useState('processing'); // processing, success, error

    const loadingTexts = [
        "Conectando aos servidores seguros...",
        "Validando identidade digital...",
        "Verificando permissões de acesso...",
        "Consultando banco de dados...",
        "Sincronizando perfil do usuário...",
        "Quase lá, finalizando ajustes...",
        "Preparando seu ambiente...",
        "Tudo pronto! Redirecionando..."
    ];

    // Efeito para ciclar as frases
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTextIndex(prev => {
                if (prev < loadingTexts.length - 1) {
                    return prev + 1;
                }
                clearInterval(interval);
                return prev;
            });
        }, 1500);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const handleCallback = async () => {
            if (processedRef.current) return;

            let accessToken = null;

            if (location.hash) {
                const params = new URLSearchParams(location.hash.replace('#', ''));
                accessToken = params.get('access_token');
            }

            if (accessToken) {
                processedRef.current = true;

                const result = await loginWithDiscord(accessToken);

                if (result.success) {
                    setStatus('success');
                    setTimeout(() => {
                        navigate('/dashboard', { replace: true });
                    }, 1000);
                } else {
                    setStatus('error');
                    setTimeout(() => {
                        if (result.requiresStaffRegister) {
                            navigate('/staff-register', {
                                state: {
                                    discordData: result.discordData,
                                    preStaffData: result.preStaffData
                                },
                                replace: true
                            });
                        } else if (result.requiresRegister) {
                            navigate('/register', {
                                state: {
                                    discordData: result.discordData,
                                    error: 'Usuário não encontrado. Por favor, complete seu cadastro.'
                                },
                                replace: true
                            });
                        } else {
                            navigate('/login', {
                                state: { error: result.error },
                                replace: true
                            });
                        }
                    }, 1500);
                }
            }
        };

        handleCallback();
    }, [location, loginWithDiscord, navigate]);

    return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 relative overflow-hidden font-sans selection:bg-blue-500/30">
            {/* Background Effects */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-blue-600/10 rounded-full blur-[120px] animate-pulse" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-purple-600/10 rounded-full blur-[120px] animate-pulse delay-700" />
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150 mix-blend-overlay" />
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="relative z-10 w-full max-w-md"
            >
                {/* Main Card */}
                <div className="bg-black/40 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-8 md:p-12 shadow-2xl shadow-black/50 relative overflow-hidden group">

                    {/* Gloss Light Effect */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000 pointer-events-none" />

                    <div className="flex flex-col items-center relative z-10">
                        {/* Animated Icon Container */}
                        <div className="relative w-32 h-32 mb-10 flex items-center justify-center">
                            {/* Outer Rings */}
                            <motion.div
                                className="absolute -inset-4 rounded-full border border-blue-500/20"
                                animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0] }}
                                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                            />
                            <motion.div
                                className="absolute -inset-8 rounded-full border border-purple-500/10"
                                animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0] }}
                                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                            />

                            {/* Main Animated Circle */}
                            <motion.div
                                className={`absolute inset-0 rounded-full bg-gradient-to-tr ${status === 'success' ? 'from-green-500 to-emerald-500' :
                                    status === 'error' ? 'from-red-500 to-orange-500' :
                                        'from-blue-600 via-purple-600 to-blue-600'
                                    } opacity-20 blur-xl transition-colors duration-500`}
                            />

                            {/* Rotating Border Ring */}
                            <motion.div
                                className={`absolute inset-0 rounded-full border-2 border-transparent ${status === 'success' ? 'border-t-green-500 border-r-emerald-500' :
                                    status === 'error' ? 'border-t-red-500 border-r-orange-500' :
                                        'border-t-blue-500 border-r-purple-500'
                                    }`}
                                animate={{ rotate: 360 }}
                                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                            />

                            {/* Inner Rotating Ring (Counter-clockwise) */}
                            <motion.div
                                className={`absolute inset-2 rounded-full border-2 border-transparent ${status === 'success' ? 'border-b-green-400 border-l-emerald-400' :
                                    status === 'error' ? 'border-b-red-400 border-l-orange-400' :
                                        'border-b-blue-400 border-l-purple-400'
                                    }`}
                                animate={{ rotate: -360 }}
                                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                            />

                            {/* Center Icon */}
                            <div className="relative z-10 bg-black/50 p-4 rounded-full backdrop-blur-md border border-white/10">
                                <AnimatePresence mode="wait">
                                    {status === 'processing' && (
                                        <motion.div
                                            key="search"
                                            initial={{ opacity: 0, scale: 0.5 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.5 }}
                                        >
                                            <Search className="w-8 h-8 text-blue-400" />
                                        </motion.div>
                                    )}
                                    {status === 'success' && (
                                        <motion.div
                                            key="success"
                                            initial={{ opacity: 0, scale: 0.5 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.5 }}
                                        >
                                            <CheckCircle2 className="w-8 h-8 text-green-400" />
                                        </motion.div>
                                    )}
                                    {status === 'error' && (
                                        <motion.div
                                            key="error"
                                            initial={{ opacity: 0, scale: 0.5 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.5 }}
                                        >
                                            <AlertCircle className="w-8 h-8 text-red-400" />
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>

                        {/* Status Text/Title */}
                        <div className="text-center h-20">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={status === 'processing' ? currentTextIndex : status}
                                    initial={{ opacity: 0, y: 10, filter: 'blur(5px)' }}
                                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                                    exit={{ opacity: 0, y: -10, filter: 'blur(5px)' }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <h2 className={`text-2xl font-bold mb-2 tracking-tight ${status === 'success' ? 'text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500' :
                                        status === 'error' ? 'text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-500' :
                                            'text-white'
                                        }`}>
                                        {status === 'processing' ? 'Autenticando' :
                                            status === 'success' ? 'Sucesso!' :
                                                'Vamos fazer seu cadastro!'}
                                    </h2>
                                    <p className={`text-sm font-medium ${status === 'success' ? 'text-green-200/60' :
                                        status === 'error' ? 'text-red-200/60' :
                                            'text-blue-200/60 animate-pulse'
                                        }`}>
                                        {status === 'processing' ? loadingTexts[currentTextIndex] :
                                            status === 'success' ? 'Entrando no sistema...' :
                                                'Usuário não encontrado. Redirecionando...'}
                                    </p>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* Enhanced Progress Bar */}
                        <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden relative">
                            {status === 'processing' && (
                                <>
                                    <motion.div
                                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600"
                                        initial={{ width: "0%" }}
                                        animate={{ width: "100%" }}
                                        transition={{ duration: 12, ease: "linear", repeat: Infinity }}
                                    />
                                    <motion.div
                                        className="absolute inset-y-0 w-20 bg-white/50 blur-lg"
                                        animate={{ x: [-100, 500] }}
                                        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                                    />
                                </>
                            )}
                            {status === 'success' && (
                                <motion.div
                                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 to-emerald-500 w-full"
                                    initial={{ width: "0%" }}
                                    animate={{ width: "100%" }}
                                    transition={{ duration: 0.5 }}
                                />
                            )}
                            {status === 'error' && (
                                <motion.div
                                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-red-500 to-orange-500 w-full"
                                    initial={{ width: "0%" }}
                                    animate={{ width: "100%" }}
                                    transition={{ duration: 0.5 }}
                                />
                            )}
                        </div>

                        <div className="mt-8 flex items-center gap-2 text-xs text-white/20 font-medium uppercase tracking-widest">
                            <Shield className="w-3 h-3" />
                            <span>Conexão Segura SSL</span>
                        </div>
                    </div>
                </div>
            </motion.div >
        </div >
    );
};

export default DiscordCallback;
